import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import {
  Button,
  Form,
  H4,
  H5,
  H6,
  IconNoteBook,
  FieldCheckbox,
  NamedLink,
  IconCalendar,
  IconCap,
  IconVisa,
  IconMasterCard,
  IconPayPal,
  IconGooglePay,
  FieldTextInput,
} from '../..';
import Switch from 'react-switch';
// Import modules from this directory
import css from './PurchaseForm.module.css';

/**
 * The PurchaseForm component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.formId] - The form id
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {boolean} [props.disabled] - Whether the form is disabled
 * @param {boolean} [props.ready] - Whether the form is ready
 * @param {Function} props.onSubmit - The submit function
 * @param {boolean} [props.invalid] - Whether the form is invalid
 * @param {boolean} [props.pristine] - Whether the form is pristine
 * @returns {JSX.Element}
 */
export const PurchaseForm = props => (
  <FinalForm
    mutators={{ ...arrayMutators }}
    {...props}
    render={formRenderProps => {
      const {
        formId = 'PurchaseForm',
        form: formApi,
        className,
        rootClassName,
        disabled,
        ready,
        handleSubmit,
        invalid,
        pristine,
        initialValues: formInitialValues,
        values: formValues,
        intl,
        data,
      } = formRenderProps;

      const classes = classNames(rootClassName || css.root, className);
      const submitReady = ready;
      const submitDisabled = invalid || disabled;
      const isBasicPackage = data?.packages?.package === 'basic';
      const hasDiscount = !isBasicPackage;

      const termsLink = (
        <NamedLink name="TermsOfServicePage" className={css.termsLink}>
          <FormattedMessage id="PackagesModal.PurchaseForm.termsAndConditions" />
        </NamedLink>
      );

      return (
        <Form onSubmit={handleSubmit} className={classes}>
          <H4 className={css.title}>
            <FormattedMessage id="PackagesModal.PurchaseForm.title" />
          </H4>
          <div className={css.overviewWrapper}>
            <div className={css.packageOverview}>
              <H6 className={css.packageOverviewHeading}>
                <FormattedMessage id="PackagesModal.PurchaseForm.packageOverview" />
              </H6>
            </div>
            <div className={css.packageDetails}>
              <div className={css.detailRow}>
                <div className={css.label}>
                  <IconNoteBook className={css.iconNoteBook} open />
                  <FormattedMessage id="PackagesModal.PurchaseForm.noOfHours" />
                </div>
                <div className={css.value}>{data?.packages?.hours}</div>
              </div>
              <div className={css.detailRow}>
                <div className={css.label}>
                  <IconCalendar className={css.iconCalendar} filled />
                  <FormattedMessage id="PackagesModal.PurchaseForm.expiresAt" />
                </div>
                <div className={css.value}>{data?.packages?.expiresAt}</div>
              </div>
            </div>
            <div className={css.breakdown}>
              <H5 className={css.toBePaid}>
                <FormattedMessage id="PackagesModal.PurchaseForm.toBePaid" />
              </H5>
              <div className={css.totalWrapper}>
                {hasDiscount ? (
                  <div className={css.subTotal}>
                    <span className={css.subTotalLabel}>
                      <FormattedMessage id="PackagesModal.PurchaseForm.subTotal" />
                    </span>
                    <span className={css.subTotalValue}>
                      {data?.packages?.subTotal?.toFixed(1)}
                    </span>
                    <span className={css.installments}>
                      <FormattedMessage id="PackagesModal.PurchaseForm.installments" />
                    </span>
                  </div>
                ) : null}
                <div className={css.total}>
                  <span className={css.totalLabel}>
                    <FormattedMessage id="PackagesModal.PurchaseForm.total" />
                  </span>
                  <span className={css.totalValue}>{data?.packages?.total?.toFixed(1)}</span>
                  <span className={css.installments}>
                    <FormattedMessage id="PackagesModal.PurchaseForm.installments" />
                  </span>
                </div>
              </div>
            </div>
            <div className={css.installmentsField}>
              <Switch
                onChange={() => formApi.change('payFullAmount', !formValues.payFullAmount)}
                checked={formValues.payFullAmount}
                uncheckedIcon={false}
                checkedIcon={false}
                offColor="#F3F5F7"
                onColor="#297DDC"
                offHandleColor="#FFFFFF"
                onHandleColor="#FFFFFF"
                handleDiameter={12}
                width={32}
                height={20}
                className={formValues.payFullAmount ? css.payFullAmountChecked : css.payFullAmount}
                activeBoxShadow={false}
              />
              <label className={css.payFullAmountLabel}>
                <FormattedMessage id="PackagesModal.PurchaseForm.payFullAmount" />
              </label>
            </div>
            <FieldTextInput
              className={css.amount}
              type="hidden"
              id="amount"
              name="amount"
              defaultValue={data?.packages?.total ? parseInt(data?.packages?.total) * 100 : 0}
            />
            <Button
              className={css.submitButton}
              type="submit"
              disabled={submitDisabled}
              ready={submitReady}
            >
              <FormattedMessage id="PackagesModal.PurchaseForm.saveActionMsg" />
            </Button>

            <div className={css.paymentMethods}>
              <FormattedMessage id="PackagesModal.PurchaseForm.acceptedMethods" />
              <IconVisa />
              <IconMasterCard />
              <IconPayPal />
              <IconGooglePay />
            </div>

            <div className={css.terms}>
              <FormattedMessage
                id="PackagesModal.PurchaseForm.terms"
                values={{ link: termsLink }}
              />
            </div>
          </div>
        </Form>
      );
    }}
  />
);

export default PurchaseForm;
